// Deal u trenéra (18. 10. 2026, schválené mockupy „Uzavřít spolupráci“, „Nezačne“, „Zatím nerozhodnuto“).
// Trenér po zkušebním tréninku: uzavře spolupráci průvodcem po krocích (Smlouva, Balíček, První trénink,
// Fakturace, Shrnutí), nebo zapíše Nezačne, nebo Nerozhodnuto. Logika na serveru: /api/deal-trener.
import React, { useEffect, useMemo, useState } from "react";
import { Handshake, UserX, UserMinus, Hourglass, ChevronLeft, ChevronRight, CircleCheck, FilePenLine, CalendarPlus, AlertTriangle, Phone, CalendarDays, MapPin, Activity } from "lucide-react";
import { Modal, HlavickaOkna } from "../ui.jsx";
import { apiFetch } from "../../lib/api.js";
import { CoRict, Volby, Cipy, Oznameni, RadekShrnuti, Fajfky, Pil, KartaObal } from "../sales/salesUi.jsx";
import { PrvniTrenink } from "../sales/NavratDialog.jsx";
import { confirmDialog } from "../confirmHost.jsx";
import PodpisQr from "../smlouva/PodpisQr.jsx";
import UdajeSmlouvy from "../smlouva/UdajeSmlouvy.jsx";

// ─── API ─────────────────────────────────────────────────────────────────────
export async function dealGet(akce, params = {}) {
  const q = new URLSearchParams({ akce, ...Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")) });
  const r = await apiFetch(`/api/deal-trener?${q.toString()}`);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
  return j;
}
export async function dealPost(akce, body = {}) {
  const r = await apiFetch("/api/deal-trener", { method: "POST", body: JSON.stringify({ akce, ...body }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || `Chyba ${r.status}`); e.kroky = j.kroky; throw e; }
  return j;
}

// ─── pomocné ─────────────────────────────────────────────────────────────────
// Koncept průvodce v zařízení (localStorage, 24 hodin). Jen pro pohodlí, server nic z něj nebere.
const KONCEPT = (id) => `fw40_deal_koncept_${id}`;
function konceptUloz(id, data) { try { localStorage.setItem(KONCEPT(id), JSON.stringify({ ...data, ulozeno: Date.now() })); } catch { /* bez úložiště */ } }
function konceptNacti(id) {
  try { const k = JSON.parse(localStorage.getItem(KONCEPT(id)) || "null"); return k && Date.now() - (k.ulozeno || 0) < 86400000 ? k : null; } catch { return null; }
}
function konceptSmaz(id) { try { localStorage.removeItem(KONCEPT(id)); } catch { /* bez úložiště */ } }
const DNY_KRATCE = ["ne", "po", "út", "st", "čt", "pá", "so"];
const dnesIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const plusDny = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dm = (iso) => { const [, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}.`; };
const dmr = (iso) => { const [y, m, d] = String(iso).split("-").map(Number); return `${d}. ${m}. ${y}`; };
const cas = (c) => String(c || "").slice(0, 5).replace(/^0(\d)/, "$1");
const kc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ").replace(/ /g, " ")} Kč`;
function kdyZkusebni(z) {
  if (!z) return "";
  const dnes = dnesIso();
  if (z.datum === dnes) return `zkušební dnes ${cas(z.cas)}`;
  if (z.datum === plusDny(dnes, -1)) return `zkušební včera ${cas(z.cas)}`;
  return `zkušební ${dm(z.datum)}`;
}
const krestni = (j) => String(j || "").trim().split(/\s+/)[0] || "";
const sTrenerem = (j) => { const k = krestni(j); if (!k) return ""; const z = /a$/i.test(k); return `s ${z ? k.replace(/a$/i, "ou") : `${k}em`}`; };

function Poznamka({ value, onChange, placeholder, label, povinna = false, id }) {
  return (
    <label className="block space-y-1.5" htmlFor={id}>
      <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">{label}{povinna && <span className="text-rose-600"> *</span>}</span>
      <textarea id={id} rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-[15px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
    </label>
  );
}

// Text kroku ze serveru: **tučně** → <strong>.
function tucne(t) {
  return String(t || "").split(/\*\*(.+?)\*\*/g).map((c, i) => (i % 2 ? <strong key={i} className="font-bold text-slate-900">{c}</strong> : <React.Fragment key={i}>{c}</React.Fragment>));
}

// Výrazná poznámka o dalších trénincích (Láďa: musí být vidět i na mobilu, proto hned nahoře kroku).
function DalsiTreninky({ hotovo = false }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border-2 border-brand bg-brand-50 px-4 py-3 text-left">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand text-white"><CalendarPlus className="h-5 w-5" /></span>
      <span className="text-[16px] font-bold leading-snug text-brand-950">{hotovo ? "Další tréninky si teď naplánuj ve svém kalendáři." : "Další tréninky můžeš naplánovat hned po uzavření spolupráce ve svém kalendáři."}</span>
    </div>
  );
}

// Hlášení #185 (30. 10. 2026, varianta A): po uzavření připomenout měření na InBody.
// Změřit jde hned po zkušebním tréninku, nebo na prvním tréninku.
function MereniInBody() {
  return (
    <div className="flex items-center gap-3 rounded-2xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-left">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white"><Activity className="h-5 w-5" /></span>
      <span className="text-[16px] font-bold leading-snug text-emerald-950">Změř klientku na InBody, hned teď, nebo na prvním tréninku.</span>
    </div>
  );
}

// ─── Nezačne ─────────────────────────────────────────────────────────────────
const DUVODY = [
  { key: "cena", label: "Cena" }, { key: "cas", label: "Čas, nestíhá" }, { key: "trener", label: "Nesedl si s trenérem" },
  { key: "forma", label: "Nesedí forma" }, { key: "zdravi", label: "Zdraví" }, { key: "dojezd", label: "Dojezd" },
  { key: "jinde", label: "Bude cvičit jinde nebo sama" }, { key: "jiny", label: "Jiný důvod" },
];
// Láďa 17. 10.: kvůli ceně nebo dojezdu je zbytečné, aby sales volala s jiným trenérem.
const BEZ_JINEHO_TRENERA = ["cena", "dojezd"];

export function NezacneDialog({ open, polozka, onClose, onHotovo }) {
  // Láďa 18. 10. 2026: dva kroky, nejdřív Proč nezačne, pak Co dál. Volby pro sales se mění podle důvodu
  // a na jedné stránce to trenéra mátlo (vybral důvod a dole zmizela možnost).
  const [krok, setKrok] = useState(0);
  const [duvod, setDuvod] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [akce, setAkce] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setKrok(0); setDuvod(""); setPoznamka(""); setAkce(""); setChyba(""); } }, [open]);
  const bezJineho = BEZ_JINEHO_TRENERA.includes(duvod);
  const moznosti = [
    ...(bezJineho ? [] : [{ key: "jiny_trener", label: "Zkusit jiného trenéra", popis: "Sales jí zavolá další pracovní den" }]),
    { key: "pozdeji", label: "Ozvat se za 3 měsíce" },
    { key: "nevolat", label: "Už jí nevolat" },
  ];
  const dal = () => { setAkce(bezJineho ? "nevolat" : ""); setKrok(1); };
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try { const r = await dealPost("nezacne", { zkusebni_id: polozka.id, duvod, akce_sales: akce, poznamka }); onHotovo?.(r); }
    catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  const nazevDuvodu = DUVODY.find((d) => d.key === duvod)?.label || "";
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={UserX} nazev="Nezačne spolupráci" popis={polozka?.jmeno} onClose={onClose} />}
      footer={krok === 0 ? <>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={dal} disabled={!duvod}>Pokračovat<ChevronRight className="h-4 w-4" /></button>
      </> : <>
        <button type="button" className="btn-secondary" onClick={() => setKrok(0)} disabled={ukladam}><ChevronLeft className="h-4 w-4" />Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!akce || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        <div className="text-[14px] font-bold text-brand-deep">Krok {krok + 1} ze 2: {krok === 0 ? "Proč nezačne" : "Co dál"}</div>
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        {krok === 0 ? (
          <>
            <div className="space-y-2">
              <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Proč nezačne</span>
              <Cipy moznosti={DUVODY} value={duvod} onChange={setDuvod} />
            </div>
            <Poznamka id="nezacne-poznamka" label="Poznámka pro sales" value={poznamka} onChange={setPoznamka} placeholder="Např. ráno jí to nevyhovuje, chtěla by spíš po 18:00." />
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5"><Pil ton="neutralni" ikona={UserX} casti={["důvod", nazevDuvodu]} mala /></div>
            <div className="space-y-2">
              <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Co dál?</span>
              <Volby name="Co dál?" moznosti={moznosti} value={akce} onChange={setAkce} />
              {bezJineho && <p className="text-[13px] text-slate-500">Kvůli ceně nebo dojezdu jiný trenér nepomůže, proto tu tahle volba není.</p>}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Nerozhodnuto ────────────────────────────────────────────────────────────
export function NerozhodnutoDialog({ open, polozka, onClose, onHotovo }) {
  const zitra = plusDny(dnesIso(), 1);
  const [datum, setDatum] = useState(zitra);
  const [proc, setProc] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  useEffect(() => { if (open) { setDatum(plusDny(dnesIso(), 1)); setProc(""); setPoznamka(""); setChyba(""); } }, [open]);
  const pozdeji = datum > zitra;
  const lzeUlozit = datum >= zitra && datum <= plusDny(dnesIso(), 30) && (!pozdeji || proc.trim().length >= 5);
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try { const r = await dealPost("nerozhodnuto", { zkusebni_id: polozka.id, datum, duvod_pozdeji: pozdeji ? proc : "", poznamka }); onHotovo?.(r); }
    catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={Hourglass} nazev="Zatím nerozhodnuto" popis={polozka?.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!lzeUlozit || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Oznameni ton="pozor"><strong>Zavolej jí nejpozději zítra.</strong> Pokud to musí být později, napiš proč. Ten den se ti hovor připomene v kartě Po zkušebním a ve Slacku.</Oznameni>
        {(Number(polozka?.nerozhodnuto_pocet) || 0) >= 1 && <Oznameni ton="problem"><strong>Tohle je poslední odklad.</strong> Při dalším hovoru musí klientka říct výsledek: uzavřít spolupráci, nebo nezačne. Řekni jí to.</Oznameni>}
        <label className="block space-y-1.5" htmlFor="nerozhodnuto-datum">
          <span className="block text-[12px] font-bold uppercase tracking-wider text-slate-500">Kdy si zavoláte</span>
          <input id="nerozhodnuto-datum" type="date" value={datum} min={zitra} max={plusDny(dnesIso(), 30)} onChange={(e) => setDatum(e.target.value)}
            className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white px-3 text-[16px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
        </label>
        {pozdeji && <Poznamka id="nerozhodnuto-proc" povinna label={`Proč až ${dmr(datum)}`} value={proc} onChange={setProc} placeholder="Např. je do pátku na dovolené." />}
        <Poznamka id="nerozhodnuto-poznamka" label="Poznámka" value={poznamka} onChange={setPoznamka} placeholder="Např. chce to probrat doma, zavolat po 18:00." />
      </div>
    </Modal>
  );
}

// ─── Nedorazila (Láďa 22. 10. 2026) ─────────────────────────────────────────
// Klientka na zkušební nepřišla. S omluvou: sales jí zavolá a domluví nový termín (kdy zavolat
// si nastaví sama). Bez omluvy: NQ lead, už ji nekontaktujeme. Zkušební zůstane v systému jako
// propadlý (pozdní zrušení). Zapsat jde i zpětně a sales může omluvu později změnit.
export const NEDORAZILA_VOLBY = [
  { key: "omluva", label: "S omluvou", popis: "Omluvila se, třeba i pozdě e-mailem nebo SMS. Sales jí zavolá a domluví nový termín." },
  { key: "bez", label: "Bez omluvy", popis: "Neozvala se. Kontakt jde do NQ lead, už ji nebudeme kontaktovat." },
];
export function NedorazilaDialog({ open, polozka, onClose, onHotovo, uloz: ulozVlastni = null, vychozi = "" }) {
  const [volba, setVolba] = useState(vychozi);
  const [poznamka, setPoznamka] = useState("");
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  // 23. 10. 2026: nezaplacený zkušební = faktura se stornuje a vždy NQ lead, omluva se nevybírá.
  const neuhrazeno = !!polozka?.neuhrazeno;
  useEffect(() => { if (open) { setVolba(vychozi); setPoznamka(""); setChyba(""); } }, [open, vychozi]);
  const lzeUlozit = !!volba && (volba !== "omluva" || poznamka.trim().length >= 3);
  const uloz = async () => {
    setUkladam(true); setChyba("");
    try {
      const telo = { zkusebni_id: polozka.id, omluva: volba === "omluva", poznamka };
      const r = ulozVlastni ? await ulozVlastni(telo) : await dealPost("nedorazila", telo);
      // 23. 10. 2026: okno drží „Ukládám…“, dokud rodič nenačte nová data, ať je změna vidět hned po zavření.
      await onHotovo?.(r);
    } catch (e) { setChyba(e.message); } finally { setUkladam(false); }
  };
  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={UserMinus} nazev="Nedorazila na zkušební" popis={polozka?.jmeno} onClose={onClose} />}
      footer={<>
        <button type="button" className="btn-secondary" onClick={onClose}>Zpět</button>
        <button type="button" className="btn-primary" onClick={uloz} disabled={!lzeUlozit || ukladam}>{ukladam ? "Ukládám…" : "Uložit"}</button>
      </>}>
      <div className="space-y-4">
        {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
        <Oznameni ton="info">Zkušební trénink zůstane v systému jako propadlý (nedorazila). Připomínky výsledku zkušebního se zastaví.</Oznameni>
        {neuhrazeno && <Oznameni ton="problem"><strong>Zkušební zatím není zaplacený.</strong> Pokud se platba neobjeví ani teď, faktura se stornuje a kontakt půjde do NQ lead, i když se omluvila.</Oznameni>}
        <Volby name="Omluvila se?" moznosti={NEDORAZILA_VOLBY} value={volba} onChange={setVolba} />
        {volba === "bez" && <Oznameni ton="problem"><strong>Kontakt půjde do NQ lead.</strong> Kdyby se později omluvila, sales to v jejím kontaktu změní na S omluvou.</Oznameni>}
        <Poznamka id="nedorazila-poznamka" povinna={volba === "omluva"} label={volba === "omluva" ? "Jak se omluvila" : "Poznámka"} value={poznamka} onChange={setPoznamka}
          placeholder={volba === "omluva" ? "Např. hodinu po tréninku napsala e-mail, že je nemocná." : "Např. nebrala telefon, nepřišla."} />
      </div>
    </Modal>
  );
}

// ─── Uzavřít spolupráci: průvodce ────────────────────────────────────────────
const KROKY = ["Smlouva", "Balíček", "První trénink", "Fakturace", "Shrnutí"];

function Kroky({ krok }) {
  return (
    <div className="mb-4 space-y-2">
      <div className="grid grid-cols-5 gap-2">
        {KROKY.map((k, i) => <span key={k} className={`h-1.5 rounded-full ${i < krok ? "bg-brand/70" : i === krok ? "bg-brand" : "bg-slate-200"}`} />)}
      </div>
      <div className="hidden grid-cols-5 gap-2 sm:grid">
        {KROKY.map((k, i) => <span key={k} className={`text-[13px] font-bold ${i === krok ? "text-brand-deep" : "text-slate-500"}`}>{i + 1}. {k}</span>)}
      </div>
      <div className="text-[14px] font-bold text-brand-deep sm:hidden">Krok {krok + 1} z 5: {KROKY[krok]}</div>
    </div>
  );
}

function popisBalicku(b) {
  if (b.typ === "PERMANENTKA") return `${kc(b.cena)} jednorázově, 10 tréninků na 4 měsíce`;
  const tydne = b.treninku_tydne === 1 ? "1 trénink týdně" : `${b.treninku_tydne} tréninky týdně`;
  if (b.typ === "FLEXIBLE") return `${kc(b.cena)} měsíčně, bez závazku, ${tydne}`;
  return `${kc(b.cena)} měsíčně, ${b.typ === "PREMIUM" ? "48" : "24"} týdnů, ${tydne}`;
}

function FakturacniUdaje({ f, email, jmeno }) {
  const adresa = [f?.ulice, f?.mesto, f?.psc].filter(Boolean).join(", ");
  return (
    <div className="rounded-2xl border border-slate-200 px-4 py-1">
      <RadekShrnuti popisek="Fakturovat na">{f?.typ === "firma" ? "Firma" : "Osoba"}</RadekShrnuti>
      {f?.typ === "firma" ? <RadekShrnuti popisek="Firma">{f.firma}{f.ico ? `, IČO ${f.ico}` : ""}</RadekShrnuti> : <RadekShrnuti popisek="Jméno">{jmeno}</RadekShrnuti>}
      <RadekShrnuti popisek="Adresa">{adresa || "chybí"}</RadekShrnuti>
      <RadekShrnuti popisek="E‑mail">{email || "chybí"}</RadekShrnuti>
    </div>
  );
}

function Pole({ id, label, value, onChange, typ = "text", autoComplete }) {
  return (
    <label className="block space-y-1" htmlFor={id}>
      <span className="block text-[13px] font-semibold text-slate-600">{label}</span>
      <input id={id} type={typ} value={value || ""} autoComplete={autoComplete} onChange={(e) => onChange(e.target.value)}
        className="min-h-[46px] w-full rounded-xl border border-slate-300 bg-white px-3 text-[16px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-200" />
    </label>
  );
}

function UpravaFakturace({ f, setF, email, setEmail }) {
  const nastav = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 p-3 sm:p-4">
      <Cipy moznosti={[{ key: "osoba", label: "Na osobu" }, { key: "firma", label: "Na firmu" }]} value={f.typ || "osoba"} onChange={(v) => setF({ ...f, typ: v || "osoba" })} />
      {f.typ === "firma" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Pole id="f-firma" label="Název firmy" value={f.firma} onChange={nastav("firma")} autoComplete="organization" />
          <Pole id="f-ico" label="IČO" value={f.ico} onChange={nastav("ico")} />
          <Pole id="f-dic" label="DIČ (nepovinné)" value={f.dic} onChange={nastav("dic")} />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-[2fr_1.4fr_1fr]">
        <Pole id="f-ulice" label="Ulice a číslo" value={f.ulice} onChange={nastav("ulice")} autoComplete="street-address" />
        <Pole id="f-mesto" label="Město" value={f.mesto} onChange={nastav("mesto")} autoComplete="address-level2" />
        <Pole id="f-psc" label="PSČ" value={f.psc} onChange={nastav("psc")} autoComplete="postal-code" />
      </div>
      <Pole id="f-email" label="E‑mail pro fakturu" typ="email" value={email} onChange={setEmail} autoComplete="email" />
    </div>
  );
}

const fakturaceKompletni = (f) => !!f && !!(f.ulice && f.mesto && f.psc) && (f.typ !== "firma" || (f.firma && f.ico));

export function DealPruvodce({ open, polozka, onClose, onHotovo, onZmena = null, zpetText = "Zpět na přehled" }) {
  const [d, setD] = useState(null);
  const [chyba, setChyba] = useState("");
  const [krok, setKrok] = useState(0);
  const [smlouva, setSmlouva] = useState("");
  const [udajeDalku, setUdajeDalku] = useState(false); // podpis na dálku: údaje do smlouvy potvrzené
  const [balicek, setBalicek] = useState("");
  const [prvni, setPrvni] = useState(null);
  const [udajeSedi, setUdajeSedi] = useState("");
  const [f, setF] = useState({ typ: "osoba" });
  const [email, setEmail] = useState("");
  const [poznamka, setPoznamka] = useState("");
  const [nahled, setNahled] = useState(null);
  const [nacitamNahled, setNacitamNahled] = useState(false);
  const [odesilam, setOdesilam] = useState(false);
  const [hotovo, setHotovo] = useState(null);
  const [obnoveno, setObnoveno] = useState(false);

  useEffect(() => {
    if (!open || !polozka) return;
    setObnoveno(false);
    setD(null); setChyba(""); setKrok(0); setSmlouva(""); setUdajeDalku(false); setBalicek(""); setPrvni(null); setUdajeSedi(""); setPoznamka(""); setNahled(null); setHotovo(null);
    dealGet("detail", { zkusebni_id: polozka.id })
      .then((x) => {
        setD(x); setF(x.fakturace || { typ: "osoba" }); if (x.nerozhodnuto) setSmlouva("na_dalku"); setEmail(x.email || ""); setUdajeSedi(fakturaceKompletni(x.fakturace) && x.email ? "" : "upravit");
        // Rozpracované uzavření (např. vypadl signál nebo selhal krok): stejné údaje se předvyplní, server pokračuje tam, kde skončil.
        const r = x.rozpracovano;
        if (r) { if (r.smlouva && (x.nerozhodnuto ? r.smlouva === "na_prvnim" : ["podepsana", "na_prvnim"].includes(r.smlouva))) setSmlouva(x.nerozhodnuto && r.na_dalku ? "na_dalku" : r.smlouva); if (r.balicek) setBalicek(r.balicek); if (r.prvni) setPrvni(r.prvni); if (r.email) setEmail(r.email); return; }
        // Koncept v zařízení (vypadl internet, zavřel okno, prohlížeč obnovil stránku): pokračuje, kde skončil.
        const k = konceptNacti(polozka.id);
        if (k) {
          // Krok se vrátí nejvýš k prvnímu, ve kterém obnovený údaj chybí nebo už neplatí.
          // 3. 11. 2026: „podepsaná“ ověří krok Smlouva u serveru (elektronický podpis), „na prvním“ jde zvolit vždy.
          // Podpis na dálku: údaje se po obnovení potvrzují znovu (krok Smlouva).
          const smlOk = k.smlouva && (x.nerozhodnuto ? k.smlouva === "na_prvnim" : ["podepsana", "na_prvnim"].includes(k.smlouva));
          const balOk = k.balicek && (x.balicky || []).some((b) => b.balicek === k.balicek);
          const prvOk = !!(k.prvni?.datum > dnesIso() && k.prvni?.cas);
          if (smlOk) setSmlouva(k.smlouva);
          if (balOk) setBalicek(k.balicek);
          if (prvOk) setPrvni(k.prvni);
          if (k.f) setF(k.f);
          if (k.email) setEmail(k.email);
          if (k.udajeSedi) setUdajeSedi(k.udajeSedi);
          if (k.poznamka) setPoznamka(k.poznamka);
          setKrok(Math.min(Number(k.krok) || 0, 3, !smlOk ? 0 : !balOk ? 1 : !prvOk ? 2 : 3));
          setObnoveno(true);
        }
      })
      .catch((e) => setChyba(e.message));
  }, [open, polozka]);

  useEffect(() => {
    // Uložit jen koncept právě načtené klientky (při přepnutí na jinou drží stav chvíli ještě předchozí).
    if (!open || !polozka || !d || hotovo || d.rozpracovano || String(d.zkusebni?.id) !== String(polozka.id)) return;
    konceptUloz(polozka.id, { krok, smlouva, balicek, prvni, f, email, udajeSedi, poznamka });
  }, [open, polozka, d, hotovo, krok, smlouva, balicek, prvni, f, email, udajeSedi, poznamka]);

  const vybrany = useMemo(() => (d?.balicky || []).find((b) => b.balicek === balicek) || null, [d, balicek]);
  const vstup = useMemo(() => ({ zkusebni_id: polozka?.id, smlouva, balicek, prvni, fakturace: f, email, poznamka }), [polozka, smlouva, balicek, prvni, f, email, poznamka]);

  // Krok 5: náhled ze serveru (stejný výpočet jako uzavření, nic se nezapisuje).
  useEffect(() => {
    if (krok !== 4) return;
    setNacitamNahled(true); setNahled(null); setChyba("");
    dealPost("nahled", vstup).then(setNahled).catch((e) => setChyba(e.message)).finally(() => setNacitamNahled(false));
  }, [krok]); // eslint-disable-line react-hooks/exhaustive-deps

  const lzeDal = [
    d?.nerozhodnuto ? smlouva === "na_prvnim" || (smlouva === "na_dalku" && udajeDalku) : smlouva === "podepsana" || smlouva === "na_prvnim",
    !!vybrany,
    !!(prvni?.datum && prvni?.cas),
    udajeSedi === "sedi" ? fakturaceKompletni(f) && /@/.test(email) : udajeSedi === "upravit" && fakturaceKompletni(f) && /@/.test(email),
    !!nahled?.lze_vystavit,
  ][krok];

  const uzavrit = async () => {
    if (!nahled?.klic || odesilam) return;
    const ok = await confirmDialog({
      title: "Uzavřít spolupráci?",
      description: `Klientce ${d.jmeno} se teď vystaví a pošle faktura ${kc(vybrany.cena)} (${vybrany.kratce}) z účtu Firma, splatnost 3 dny. Opravdu uzavřít?`,
      confirmLabel: "Ano, uzavřít a vystavit fakturu", cancelLabel: "Zpět", variant: "primary",
    });
    if (!ok) return;
    setOdesilam(true); setChyba("");
    try { const r = await dealPost("uzavrit", { ...vstup, klic: nahled.klic }); konceptSmaz(polozka.id); setHotovo(r); onHotovo?.(r); }
    catch (e) { setChyba(e.message); if (e.kroky) { konceptSmaz(polozka.id); setHotovo({ chyba: e.message, kroky: e.kroky }); onZmena?.(); } }
    finally { setOdesilam(false); }
  };

  const popis = d ? `${d.jmeno}, ${kdyZkusebni(d.zkusebni)} ${sTrenerem(d.trener?.jmeno)}` : polozka?.jmeno;
  const header = <HlavickaOkna ikona={Handshake} nazev="Uzavřít spolupráci" popis={popis} onClose={onClose} />;

  if (hotovo && !hotovo.chyba) {
    const h = hotovo.hotovo || {};
    const ok = (hotovo.kroky || []).filter((k) => k.ok);
    const spatne = (hotovo.kroky || []).filter((k) => !k.ok);
    return (
      <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka header={header}
        footer={<button type="button" className="btn-primary" onClick={onClose}>{zpetText}</button>}>
        <div className="mx-auto max-w-xl space-y-4 py-2 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CircleCheck className="h-9 w-9" /></span>
          <h3 className="font-serif text-2xl font-bold text-slate-900">Spolupráce uzavřena</h3>
          <p className="text-[16px] text-slate-700"><strong>{h.jmeno}</strong>, {h.balicek}, první trénink <strong>{h.prvni ? `${DNY_KRATCE[new Date(`${h.prvni.datum}T12:00:00Z`).getUTCDay()]} ${dm(h.prvni.datum)} v ${cas(h.prvni.cas)}` : ""}</strong></p>
          <div className="rounded-2xl border border-slate-200 p-4 text-left"><Fajfky polozky={ok.map((k) => tucne(k.text))} /></div>
          {spatne.length > 0 && (
            <div className="space-y-2 text-left">{spatne.map((k, i) => <Oznameni key={i} ton="pozor">{tucne(k.text)}</Oznameni>)}</div>
          )}
          <MereniInBody />
          <DalsiTreninky hotovo />
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} size="lg" plne plnaHlavicka header={header}
      footer={<>
        <button type="button" className="btn-secondary" onClick={krok === 0 ? onClose : () => setKrok(krok - 1)} disabled={odesilam}>
          <ChevronLeft className="h-4 w-4" />{krok === 0 ? "Zavřít" : "Zpět"}
        </button>
        {krok < 4
          ? <button type="button" className="btn-primary" disabled={!lzeDal} onClick={() => setKrok(krok + 1)}>Pokračovat<ChevronRight className="h-4 w-4" /></button>
          : <button type="button" className="btn-primary !bg-emerald-600 hover:!bg-emerald-700" disabled={!lzeDal || odesilam} onClick={uzavrit}><Handshake className="h-4 w-4" />{odesilam ? "Uzavírám…" : "Uzavřít spolupráci"}</button>}
      </>}>
      <Kroky krok={krok} />
      {chyba && <Oznameni ton="problem" className="mb-3">{chyba}</Oznameni>}
      {hotovo?.chyba && <div className="mb-3 space-y-2"><Fajfky polozky={(hotovo.kroky || []).filter((k) => k.ok).map((k) => tucne(k.text))} /></div>}
      {!d ? (!chyba && <div className="py-10 text-center text-slate-500">Načítám…</div>) : (
        <div className="space-y-4">
          {obnoveno && !d.rozpracovano && !hotovo && <Oznameni ton="znacka"><strong>Pokračuješ, kde jsi skončil.</strong> Vyplněné údaje zůstaly uložené.</Oznameni>}
          {d.rozpracovano && !hotovo && <Oznameni ton="pozor"><strong>Uzavření se minule nedokončilo.</strong> Údaje jsou předvyplněné, projdi je a dokonči. Co už proběhlo, se nezopakuje.</Oznameni>}
          {krok === 0 && (
            <>
              {/* Láďa 18. 10. 2026: po Nerozhodnuto se uzavírá po telefonu, jediná volba je podpis na prvním tréninku. */}
              {d.nerozhodnuto ? (
                <>
                  {/* 3. 11. 2026 (schváleno): podpis na dálku, odkaz přijde po uzavření SMS a e‑mailem. */}
                  <CoRict>{smlouva === "na_dalku" ? "Smlouvu vám hned pošlu do telefonu SMS i e‑mailem. Můžete si ji při hovoru projít a rovnou podepsat." : "Smlouvu podepíšete na prvním tréninku."}</CoRict>
                  <Volby name="Smlouva" value={smlouva} onChange={(v) => { setSmlouva(v); setUdajeDalku(false); }} moznosti={[
                    { key: "na_dalku", label: "Poslat k podpisu do telefonu", popis: "Po uzavření jí přijde SMS a e‑mail, podepíše hned při hovoru" },
                    { key: "na_prvnim", label: "Podepíše na prvním tréninku", popis: "Uzavíráš po telefonu, klientka u tebe není" },
                  ]} />
                  {smlouva === "na_dalku" && (
                    <UdajeSmlouvy klic={{ zkusebni_id: polozka.id }} rezim="ulozit" tlacitko="Údaje sedí" potvrzeno={udajeDalku}
                      onPotvrzeno={(u) => { setUdajeDalku(true); if (u?.email) setEmail(u.email); }} onZmena={() => setUdajeDalku(false)} />
                  )}
                </>
              ) : d.rozpracovano?.smlouva === "podepsana" ? (
                // Uzavření rozpracované ještě před elektronickým podpisem: smlouva už je podepsaná, nic nového.
                <Oznameni ton="ok"><strong>Smlouva je podepsaná.</strong> Pokračuj a dokonči uzavření.</Oznameni>
              ) : smlouva === "na_prvnim" ? (
                <>
                  <CoRict>Smlouvu podepíšete na prvním tréninku.</CoRict>
                  <button type="button" className="btn-secondary w-full justify-center sm:w-auto" onClick={() => setSmlouva("")}><FilePenLine className="h-4 w-4" />Podepsat teď na tabletu</button>
                </>
              ) : (
                <>
                  {/* 3. 11. 2026 (schválený mockup): podpis na tabletu přes QR kód, potvrzení SMS v telefonu klientky. */}
                  <CoRict>Teď spolu podepíšeme smlouvu. Na tabletu si ji v klidu přečtěte, podpis pak potvrdíte ve svém telefonu přes SMS.</CoRict>
                  <PodpisQr zkusebniId={polozka.id} onUdaje={(u) => { if (u?.email) setEmail(u.email); }}
                    onStav={(p) => { if (p?.stav === "podepsano") setSmlouva("podepsana"); else setSmlouva((x) => (x === "podepsana" ? "" : x)); }} />
                  {smlouva !== "podepsana" && (
                    <div className="border-t border-slate-100 pt-3">
                      <button type="button" className="btn-secondary w-full justify-center sm:w-auto" onClick={() => setSmlouva("na_prvnim")}>Podepíše na prvním tréninku</button>
                    </div>
                  )}
                </>
              )}
              {smlouva === "na_prvnim" && <Oznameni ton="znacka" ikona={FilePenLine}><strong>Faktura i všechno ostatní se vystaví hned.</strong> První trénink v kalendáři dostane štítek <strong>Podepsat smlouvu</strong>. Na tréninku ho otevři v kalendáři, klepni na <strong>Podepsat na tabletu</strong> a QR kód načti tabletem.</Oznameni>}
            </>
          )}
          {krok === 1 && (
            <>
              <CoRict>Jaký balíček si {d.muz ? "vybral" : "vybrala"}?</CoRict>
              <Volby name="Balíček" value={balicek} onChange={setBalicek} moznosti={d.balicky.map((b) => ({ key: b.balicek, label: b.kratce, popis: popisBalicku(b) }))} />
              <p className="text-[13px] text-slate-500">Ceny jsou z produktů ve FAPI, fakturuje se z účtu Firma.</p>
            </>
          )}
          {krok === 2 && (
            <>
              <CoRict>Kdy bude první trénink? Vyber volný čas ve svém kalendáři.</CoRict>
              <DalsiTreninky />
              <PrvniTrenink predbezne={{ trener_id: d.trener.id, trener: d.trener.jmeno, casy: [], putujici: null }} lokalita={d.zkusebni.lokalita}
                value={prvni} onChange={setPrvni} nacti={({ mesic }) => dealGet("terminy", { zkusebni_id: polozka.id, mesic })} />
            </>
          )}
          {krok === 3 && (
            <>
              <CoRict>Zkontroluj s {d.muz ? "klientem" : "klientkou"} fakturační údaje.</CoRict>
              {d.fakturace && fakturaceKompletni(d.fakturace) && (
                <>
                  <FakturacniUdaje f={d.fakturace} email={d.email} jmeno={d.jmeno} />
                  <p className="text-[13px] text-slate-500">Zadala je sales při domluvení zkušebního.</p>
                  <Volby name="Fakturační údaje" value={udajeSedi} onChange={(v) => { setUdajeSedi(v); if (v === "sedi") { setF(d.fakturace); setEmail(d.email); } }}
                    moznosti={[{ key: "sedi", label: "Údaje sedí" }, { key: "upravit", label: "Upravit nebo fakturovat na firmu" }]} />
                </>
              )}
              {(udajeSedi === "upravit" || !(d.fakturace && fakturaceKompletni(d.fakturace))) && <UpravaFakturace f={f} setF={setF} email={email} setEmail={setEmail} />}
              <Oznameni ton="info">Splatnost faktury je <strong>3 dny</strong>. Když {d.muz ? "klient" : "klientka"} chce delší, napiš to Kristině do poznámky, domluví se s {d.muz ? "ním" : "ní"}.</Oznameni>
              <Poznamka id="deal-poznamka" label="Poznámka pro Kristinu (nepovinné)" value={poznamka} onChange={setPoznamka} placeholder="Např. chce splatnost až k 15., nastupuje později, výjimka v ceně." />
            </>
          )}
          {krok === 4 && (
            nacitamNahled ? <div className="py-10 text-center text-slate-500">Počítám shrnutí…</div> : nahled && (
              <>
                {!nahled.lze_vystavit && (
                  <Oznameni ton="problem" ikona={AlertTriangle}><strong>Fakturu teď nejde vystavit.</strong> {nahled.blokace.join(" ")} Napiš Kristině.</Oznameni>
                )}
                <div className="rounded-2xl border border-slate-200 px-4 py-1">
                  <RadekShrnuti popisek="Smlouva">{nahled.shrnuti.smlouva}</RadekShrnuti>
                  <RadekShrnuti popisek="Balíček">{nahled.shrnuti.balicek}</RadekShrnuti>
                  <RadekShrnuti popisek="První trénink">{nahled.shrnuti.prvni_trenink}</RadekShrnuti>
                  <RadekShrnuti popisek="Spolupráce">{nahled.shrnuti.spoluprace}</RadekShrnuti>
                  <RadekShrnuti popisek="První faktura">{nahled.shrnuti.prvni_faktura}</RadekShrnuti>
                  <RadekShrnuti popisek="Další faktury">{nahled.shrnuti.dalsi_faktury}</RadekShrnuti>
                  <RadekShrnuti popisek="Fakturovat na">{nahled.shrnuti.fakturovat_na}</RadekShrnuti>
                  {poznamka && <RadekShrnuti popisek="Poznámka pro Kristinu">{poznamka}</RadekShrnuti>}
                </div>
              </>
            )
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── Karta „Po zkušebním: rozhodni“ ──────────────────────────────────────────
// Láďa 18. 10. 2026: údaje v pilulkách, ať jsou čitelné. Nerozhodnuto jde jen jednou navíc (celkem dvakrát).
const MAX_NEROZHODNUTO = 2;
const lzeOdlozit = (p) => (Number(p?.nerozhodnuto_pocet) || 0) < MAX_NEROZHODNUTO;
function zkusebniCasti(p) {
  const dnes = dnesIso();
  const den = p.datum === dnes ? "dnes" : p.datum === plusDny(dnes, -1) ? "včera" : dm(p.datum);
  return ["zkušební", den, cas(p.cas)];
}
function PilulkyZkusebni({ p }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Pil ton="neutralni" ikona={CalendarDays} casti={zkusebniCasti(p)} mala />
      <Pil ton="neutralni" ikona={MapPin} casti="VT GYM" mala />
      {p.vysledek === "nerozhodnuto" && <Pil ton={p.dnes_volat ? "problem" : "pozor"} ikona={p.dnes_volat ? Phone : Hourglass} casti={p.dnes_volat ? ["dnes zavolat", p.telefon] : ["rozhodne se", dm(p.nerozhodnuto_datum)]} mala />}
    </div>
  );
}
// 22. 10. 2026: čtvrtá volba Nedorazila. Na telefonu tři vedlejší tlačítka vedle sebe (ikona nad
// textem jako v sales), Uzavřít přes celou šířku.
const VEDLEJSI = "btn-secondary justify-center !min-h-[64px] !flex-col !gap-1 !px-1 !text-[13px] lg:!min-h-[44px] lg:!flex-row lg:!gap-2 lg:!px-4 lg:!text-[14px]";
const SLOUPCE = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" };
function TlacitkaRozhodnuti({ p, onOtevri }) {
  const odlozit = lzeOdlozit(p);
  // U Nerozhodnuto klientka na zkušebním byla, Nedorazila tam nedává smysl.
  const nedorazila = p.vysledek !== "nerozhodnuto";
  const vedlejsich = 1 + (odlozit ? 1 : 0) + (nedorazila ? 1 : 0);
  return (
    <div className="space-y-2">
      {!odlozit && <Oznameni ton="pozor"><strong>Klientka teď musí říct výsledek.</strong> Rozhodnutí už se jednou odkládalo, další Nerozhodnuto nejde.</Oznameni>}
      <div className={`grid ${SLOUPCE[vedlejsich]} gap-2 lg:flex lg:flex-nowrap lg:justify-end`}>
        {nedorazila && <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nedorazila", polozka: p })}><UserMinus className="h-5 w-5 lg:h-4 lg:w-4" />Nedorazila</button>}
        <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nezacne", polozka: p })}><UserX className="h-5 w-5 lg:h-4 lg:w-4" />Nezačne</button>
        {odlozit && <button type="button" className={VEDLEJSI} onClick={() => onOtevri({ typ: "nerozhodnuto", polozka: p })}><Hourglass className="h-5 w-5 lg:h-4 lg:w-4" />Nerozhodnuto</button>}
        <button type="button" className="btn-primary col-span-full justify-center" onClick={() => onOtevri({ typ: "uzavrit", polozka: p })}><Handshake className="h-4 w-4" />Uzavřít spolupráci</button>
      </div>
    </div>
  );
}
export default function KartaPoZkusebnim({ trenerId = null, trener = null, className = "", onPocet = null }) {
  const [polozky, setPolozky] = useState(null);
  const [otevreno, setOtevreno] = useState(null); // { typ, polozka }
  // trener = jméno (owner v náhledu trenéra na přehledu), server ho bere jen od vedení.
  const nacti = () => dealGet("po_zkusebnim", trenerId ? { trener_id: trenerId } : trener ? { trener } : {}).then((r) => { setPolozky(r.polozky || []); onPocet?.((r.polozky || []).length); }).catch(() => { setPolozky([]); onPocet?.(0); });
  useEffect(() => { nacti(); }, [trenerId, trener]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!polozky || (!polozky.length && !otevreno)) return null;
  // Okno se zavře až s novým seznamem, ať vyřízený zkušební hned zmizí (23. 10. 2026).
  const hotovo = async () => { await nacti(); setOtevreno(null); };
  const zavri = () => { setOtevreno(null); nacti(); };
  return (
    <>
      <KartaObal className={className}>
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
          <Handshake className="h-5 w-5 text-brand" />
          <h3 className="min-w-0 flex-1 text-[16px] font-bold text-slate-900">Po zkušebním: rozhodni</h3>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[12px] font-bold tabular-nums text-amber-800">{polozky.length}</span>
        </div>
        {polozky.map((p) => (
          <div key={p.id} className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 lg:flex-row lg:items-center">
            <div className="min-w-0 flex-1 space-y-1.5">
              <div className="text-[16px] font-bold text-brand-deep">{p.jmeno}</div>
              <PilulkyZkusebni p={p} />
            </div>
            {/* Mobil: dvě vedlejší tlačítka vedle sebe, Uzavřít přes celou šířku (schváleno 17. 10.). */}
            <div className="lg:min-w-[440px]"><TlacitkaRozhodnuti p={p} onOtevri={setOtevreno} /></div>
          </div>
        ))}
      </KartaObal>
      <DealPruvodce key={otevreno?.polozka?.id || "zavreno"} open={otevreno?.typ === "uzavrit"} polozka={otevreno?.polozka} onClose={zavri} />
      <NezacneDialog open={otevreno?.typ === "nezacne"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
      <NerozhodnutoDialog open={otevreno?.typ === "nerozhodnuto"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
      <NedorazilaDialog open={otevreno?.typ === "nedorazila"} polozka={otevreno?.polozka} onClose={zavri} onHotovo={hotovo} />
    </>
  );
}

// ─── Okno zkušebního tréninku v kalendáři (Láďa 18. 10. 2026) ────────────────
// Po začátku zkušebního ukáže stejná tři tlačítka jako karta na přehledu. Dialog otevírá rodič
// (okno rezervace se místo sebe přepne na průvodce, dvě okna přes sebe nejsou).
export function RozhodnutiZkusebni({ bookingId, onOtevri }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!bookingId) return undefined;
    let zive = true;
    dealGet("zkusebni", { booking_id: bookingId }).then((r) => { if (zive) setSt(r); }).catch(() => {});
    return () => { zive = false; };
  }, [bookingId]);
  if (!st?.polozka) return null;
  const p = st.polozka;
  if (st.vysledek === "uzavreno") return <Oznameni ton="ok" ikona={CircleCheck}><strong>Spolupráce uzavřena.</strong></Oznameni>;
  if (st.vysledek === "nezacne") return <Oznameni ton="info" ikona={UserX}><strong>Nezačne spolupráci.</strong></Oznameni>;
  if (st.vysledek === "nedorazila") return <Oznameni ton={st.omluva ? "pozor" : "problem"} ikona={UserMinus}><strong>Nedorazila {st.omluva ? "s omluvou" : "bez omluvy"}.</strong> {st.omluva ? "Sales jí zavolá kvůli novému termínu." : "Kontakt je v NQ lead."}</Oznameni>;
  // 22. 10. 2026: před koncem zkušebního (od 24 hodin před začátkem) jde zapsat jen Nedorazila.
  if (!st.muze_rozhodnout && st.muze_nedorazila) {
    return (
      <button type="button" className="btn w-full justify-center border border-rose-300 bg-white text-rose-700 !min-h-[48px]" onClick={() => onOtevri({ typ: "nedorazila", polozka: p })}>
        <UserMinus className="h-4 w-4" />Nedorazila na zkušební
      </button>
    );
  }
  if (!st.muze_rozhodnout) return null;
  return (
    <div className="space-y-2.5 rounded-2xl border border-brand/25 bg-brand-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Handshake className="h-5 w-5 text-brand" />
        <span className="text-[15px] font-bold text-slate-900">Po zkušebním: rozhodni</span>
        {p.vysledek === "nerozhodnuto" && <Pil ton={p.dnes_volat ? "problem" : "pozor"} ikona={p.dnes_volat ? Phone : Hourglass} casti={p.dnes_volat ? ["dnes zavolat", p.telefon] : ["rozhodne se", dm(p.nerozhodnuto_datum)]} mala />}
      </div>
      <TlacitkaRozhodnuti p={p} onOtevri={onOtevri} />
    </div>
  );
}

// Dialog vybraný v okně zkušebního. Po zápisu zavolá onHotovo (rodič obnoví kalendář a zavře okno),
// zavření bez zápisu vrátí okno rezervace (onZpet).
export function DialogZkusebni({ volba, onZpet, onHotovo }) {
  const [hotovo, setHotovo] = useState(false);
  if (!volba) return null;
  if (volba.typ === "uzavrit") return <DealPruvodce open polozka={volba.polozka} zpetText="Zavřít" onClose={() => (hotovo ? onHotovo() : onZpet())} onHotovo={() => setHotovo(true)} onZmena={() => setHotovo(true)} />;
  if (volba.typ === "nezacne") return <NezacneDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
  if (volba.typ === "nedorazila") return <NedorazilaDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
  return <NerozhodnutoDialog open polozka={volba.polozka} onClose={onZpet} onHotovo={onHotovo} />;
}

// ─── Nepodepsaná smlouva po uzavření po telefonu (Láďa 18. 10. 2026) ─────────
// Červený štítek s mírně pulzující tečkou, ať si ho trenér všimne. Stejný v kalendáři i v kartě klientky.
export function StitekPodepsat({ mala = false }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-rose-600 font-bold text-white ${mala ? "px-1.5 py-px text-[10px] uppercase tracking-wider" : "px-2.5 py-1 text-[13px]"}`}>
      <span className="relative flex h-2 w-2 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-white" /></span>
      <FilePenLine className={mala ? "h-2.5 w-2.5 shrink-0" : "h-3.5 w-3.5 shrink-0"} />Podepsat smlouvu
    </span>
  );
}

// Záloha „Podepsáno na papíře“ platí do 31. 12. 2026 (pražský čas), pak zmizí.
const PAPIR_DO = "2026-12-31";
function papirPovolen() {
  const dnes = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Prague" }).format(new Date());
  return dnes <= PAPIR_DO;
}

// Blok s tlačítkem Podepsat na tabletu (okno tréninku i karta klientky). 3. 11. 2026: podpis přes QR kód
// na tabletu a SMS v telefonu klientky, po podpisu se smlouva potvrdí sama. Ruční potvrzení zůstává
// pro smlouvu podepsanou jinak (papír).
export function BlokPodepsat({ dealId, kdy = null, onHotovo, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");
  const [tablet, setTablet] = useState(false);
  const potvrdit = async () => {
    setBusy(true); setChyba("");
    try { await dealPost("smlouva_podepsana", { deal_id: dealId }); onHotovo?.(); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`space-y-2.5 rounded-2xl border border-rose-200 bg-rose-50 p-3 ${className}`}>
      <StitekPodepsat />
      <div className="text-[14px] leading-snug text-rose-950">Smlouvu klientka podepíše na prvním tréninku{kdy ? <> <strong>{kdy}</strong></> : null}. Klepni na <strong>Podepsat na tabletu</strong> a QR kód načti tabletem.</div>
      {chyba && <Oznameni ton="problem">{chyba}</Oznameni>}
      <button type="button" className="btn w-full min-h-[52px] justify-center bg-emerald-600 text-base text-white hover:bg-emerald-700" onClick={() => setTablet(true)}><FilePenLine className="h-4 w-4" />Podepsat na tabletu</button>
      {/* Láďa 3. 11. 2026: ruční potvrzení papírové smlouvy jen jako záloha do konce roku 2026. */}
      {papirPovolen() && <button type="button" className="btn-secondary w-full justify-center" onClick={potvrdit} disabled={busy}><CircleCheck className="h-4 w-4" />{busy ? "Ukládám…" : "Podepsáno na papíře"}</button>}
      {tablet && (
        <Modal open onClose={() => setTablet(false)} size="md" plnaHlavicka header={<HlavickaOkna ikona={FilePenLine} nazev="Podepsat smlouvu" popis="QR kód načti tabletem" onClose={() => setTablet(false)} />}
          footer={<button type="button" className="btn-secondary" onClick={() => setTablet(false)}>Zavřít</button>}>
          <div className="space-y-4">
            <CoRict>Teď spolu podepíšeme smlouvu. Na tabletu si ji v klidu přečtěte, podpis pak potvrdíte ve svém telefonu přes SMS.</CoRict>
            <PodpisQr dealId={dealId} onPodepsano={() => { setTimeout(() => { setTablet(false); onHotovo?.(); }, 2500); }} />
          </div>
        </Modal>
      )}
    </div>
  );
}

// Karta klientky: blok se ukáže jen trenérovi té klientky (a vedení), když smlouva čeká na podpis.
export function PodpisSmlouvyKlientky({ klientId, className = "" }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!klientId) return undefined;
    let zive = true;
    dealGet("smlouva_klientky", { klient_id: klientId }).then((r) => { if (zive) setSt(r.smlouva || null); }).catch(() => {});
    return () => { zive = false; };
  }, [klientId]);
  if (!st) return null;
  const kdy = st.datum ? `${DNY_KRATCE[new Date(`${st.datum}T12:00:00Z`).getUTCDay()]} ${dm(st.datum)}${st.cas ? ` v ${cas(st.cas)}` : ""}` : null;
  return <BlokPodepsat dealId={st.deal_id} kdy={kdy} className={className} onHotovo={() => setSt(null)} />;
}
